// src/App.jsx — Closet Collective v3.1
import { useState, useRef, useEffect } from "react";
import ImageUploader from "./components/ImageUploader";
import PlatformStatus from "./components/PlatformStatus";
import PostButton from "./components/PostButton";
import {
  fetchListings, saveListing, updateStatus, recordSale, deleteListing, updateListing,
  fetchMembers, addMember,
  savePayoutSplits, clearPayoutSplits, fetchPayoutSplits, markSplitPaid, markMemberPaid, fetchMemberEarnings,
} from "./lib/supabase";
import { getConnectionStatus } from "./lib/api";

const PLATFORMS = {
  depop:    { name: "Depop",                fee: "10%",      feeRate: 0.10, color: "#FF2300", icon: "D", url: "https://depop.com/sell" },
  mercari:  { name: "Mercari",              fee: "0%",       feeRate: 0.00, color: "#E74040", icon: "M", url: "https://mercari.com/sell" },
  etsy:     { name: "Etsy",               fee: "5%+$0.20",  feeRate: 0.08, color: "#F1641E", icon: "E", url: "https://etsy.com/sell", flatFee: 0.20 },
  grailed:  { name: "Grailed",            fee: "~9%",       feeRate: 0.09, color: "#8B5CF6", icon: "G", url: "https://grailed.com/sell" },
  ebay:     { name: "eBay",              fee: "~13%",        feeRate: 0.13, color: "#0064D2", icon: "e", url: "https://ebay.com/sell" },
  facebook: { name: "Facebook Marketplace", fee: "Free",    feeRate: 0.00, color: "#1877F2", icon: "F", url: "https://facebook.com/marketplace" },
};

const SHIPPING = [
  { name: "USPS First Class (< 1 lb)",    cost: 4.50  },
  { name: "USPS Priority Mail (1–3 lb)",  cost: 9.85  },
  { name: "USPS Priority Mail (3–5 lb)",  cost: 13.75 },
  { name: "USPS Ground Advantage (1 lb)", cost: 5.50  },
  { name: "USPS Ground Advantage (3 lb)", cost: 8.95  },
  { name: "UPS Ground (1 lb)",            cost: 9.25  },
  { name: "UPS Ground (5 lb)",            cost: 13.50 },
  { name: "FedEx Home Delivery (1 lb)",   cost: 10.20 },
  { name: "FedEx Home Delivery (5 lb)",   cost: 14.75 },
  { name: "Local Pickup (free)",          cost: 0.00  },
];

// Status config — single source of truth for colours, labels, badges
const STATUS_CONFIG = {
  available:  { label: "Available",  badgeBg: "var(--surface2)", badgeColor: "var(--muted)",  opacity: 1 },
  sold:       { label: "Sold",       badgeBg: "var(--green-l)",  badgeColor: "var(--green)",  opacity: 0.6 },
  cancelled:  { label: "Cancelled",  badgeBg: "#FEF0E8",         badgeColor: "#C8874A",        opacity: 0.5 },
};

const BLANK = {
  sellerName: "", sellerId: "", itemName: "", category: "", gender: "",
  brand: "", era: "", size: "", condition: "",
  material: "", color: "", description: "", askingPrice: "", itemCost: "",
};

// Compute realized profit for a sold listing (falls back to suggested price if no actual sale price)
function listingProfit(i) {
  const sale = i.sale_price != null ? Number(i.sale_price) : parsePrice(i.suggested_price);
  const fee = Number(i.platform_fee || 0);
  const ship = Number(i.shipping_cost || 0);
  const cost = Number(i.item_cost || 0);
  return sale - fee - ship - cost;
}

const LOAD_MSGS = [
  "Reading your item details…",
  "Matching to the best platform…",
  "Crafting your listing copy…",
  "Almost ready…",
];

function initials(name) {
  if (!name) return "?";
  return name.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function parsePrice(str) {
  if (!str) return 0;
  return parseFloat(String(str).replace(/[^0-9.]/g, "")) || 0;
}

// Normalise legacy items that only have sold boolean, no status field
function normaliseItem(item) {
  if (item.status) return item;
  return { ...item, status: item.sold ? "sold" : "available" };
}

const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&family=Epilogue:wght@300;400;500;600&display=swap');
:root{--bg:#F7F3EE;--surface:#FFFFFF;--surface2:#F2EDE6;--border:#E4DDD4;--border2:#D4CBBF;--text:#1A1714;--muted:#7A6F64;--accent:#C8874A;--accent-l:#F5E8D8;--green:#3D7A52;--green-l:#DFF0E6;--red:#C0392B;}
[data-theme="dark"]{--bg:#151210;--surface:#1E1B18;--surface2:#252119;--border:#35302A;--border2:#453E36;--text:#F0EAE2;--muted:#8C8078;--accent:#D4964E;--accent-l:#2E2218;--green:#4DA867;--green-l:#1A2E22;--red:#E05252;}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}
body{background:var(--bg);font-family:'Epilogue',sans-serif;color:var(--text);-webkit-font-smoothing:antialiased;transition:background .3s,color .3s;}
.app{min-height:100vh;padding-bottom:5rem;}
.header{background:var(--text);padding:1.75rem 1.5rem 2.5rem;position:relative;overflow:hidden;}
.header-wave{position:absolute;bottom:-1px;left:0;right:0;height:36px;background:var(--bg);border-radius:36px 36px 0 0;}
.header-top{display:flex;justify-content:space-between;align-items:flex-start;}
.header-eyebrow{font-size:10px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--accent);margin-bottom:10px;}
.header-title{font-family:'Playfair Display',serif;font-size:clamp(2rem,6vw,2.8rem);color:#F7F3EE;line-height:1.1;}
.header-title em{font-style:italic;color:var(--accent);}
.header-sub{font-size:13px;color:#7A6F64;font-weight:300;margin-top:8px;}
.theme-btn{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);color:#A89F8C;border-radius:50%;width:36px;height:36px;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .2s;}
.theme-btn:hover{background:rgba(255,255,255,.15);}
.main{max-width:740px;margin:0 auto;padding:1.75rem 1.25rem 0;}
.tab-row{display:flex;gap:4px;margin-bottom:1.5rem;overflow-x:auto;}
.tab{flex:1;min-width:0;padding:9px 6px;border-radius:12px;text-align:center;font-size:12px;font-weight:500;cursor:pointer;border:1px solid transparent;transition:all .15s;color:var(--muted);background:transparent;font-family:'Epilogue',sans-serif;white-space:nowrap;}
.tab.active{background:var(--text);color:var(--bg);}
.tab:not(.active):hover{background:var(--surface2);border-color:var(--border);}
.section-label{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-bottom:10px;}
.form-card{background:var(--surface);border-radius:20px;padding:1.5rem;margin-bottom:1.25rem;border:1px solid var(--border);}
.field{margin-bottom:1rem;}.field:last-child{margin-bottom:0;}
label{display:block;font-size:12px;font-weight:500;color:var(--muted);margin-bottom:5px;letter-spacing:.04em;}
input[type=text],input[type=number],textarea,select{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:10px 13px;font-family:'Epilogue',sans-serif;font-size:14px;color:var(--text);outline:none;transition:border-color .15s,background .15s;appearance:none;}
input[type=text]:focus,input[type=number]:focus,textarea:focus,select:focus{border-color:var(--accent);background:var(--surface);}
textarea{resize:vertical;min-height:80px;line-height:1.5;}
.row2{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.row3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;}
.platform-status-grid{display:flex;flex-direction:column;gap:10px;}
.platform-status-card{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;gap:12px;transition:border-color .15s;}
.platform-status-card.connected{border-color:var(--green);}
.psc-left{display:flex;align-items:center;gap:10px;}
.psc-icon{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;flex-shrink:0;}
.psc-name{font-size:14px;font-weight:600;color:var(--text);}
.psc-desc{font-size:11px;color:var(--muted);margin-top:1px;}
.psc-btn{font-size:12px;font-weight:500;padding:6px 12px;border-radius:8px;cursor:pointer;font-family:'Epilogue',sans-serif;transition:all .12s;white-space:nowrap;}
.psc-btn.connect{background:transparent;border:1px solid;}
.psc-btn.connect:hover{opacity:.7;}
.psc-btn.disconnect{background:var(--surface2);border:1px solid var(--border);color:var(--muted);}
.psc-btn.disconnect:hover{background:var(--border);color:var(--text);}
.image-uploader{margin-top:0;}
.image-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:8px;margin-bottom:10px;}
.image-thumb{position:relative;border-radius:10px;overflow:hidden;aspect-ratio:1;border:1px solid var(--border);}
.image-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
.image-remove{position:absolute;top:4px;right:4px;background:rgba(0,0,0,.55);color:#fff;border:none;border-radius:50%;width:20px;height:20px;cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;}
.image-main-badge{position:absolute;bottom:4px;left:4px;background:var(--accent);color:#fff;font-size:9px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;padding:2px 6px;border-radius:4px;}
.drop-zone{border:1.5px dashed var(--border2);border-radius:12px;padding:20px 16px;text-align:center;cursor:pointer;transition:border-color .15s,background .15s;}
.drop-zone:hover,.drop-zone.drag-over{border-color:var(--accent);background:var(--accent-l);}
.drop-zone-inner{display:flex;flex-direction:column;align-items:center;gap:6px;}
.drop-icon{font-size:22px;color:var(--muted);}
.drop-label{font-size:13px;font-weight:500;color:var(--text);}
.drop-sub{font-size:11px;color:var(--muted);}
.upload-progress-list{width:100%;display:flex;flex-direction:column;gap:8px;}
.upload-progress-row{display:flex;align-items:center;gap:8px;}
.upload-filename{font-size:12px;color:var(--muted);min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;flex:1;}
.upload-bar{flex:2;height:4px;background:var(--border);border-radius:99px;overflow:hidden;}
.upload-bar div{height:100%;background:var(--accent);border-radius:99px;transition:width .15s;}
.upload-pct{font-size:11px;color:var(--muted);min-width:30px;text-align:right;}
code{font-size:11px;background:var(--surface2);padding:1px 5px;border-radius:4px;font-family:monospace;}
.analyze-btn{width:100%;background:var(--text);color:var(--bg);border:none;border-radius:14px;padding:15px;font-family:'Epilogue',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .1s;letter-spacing:.02em;margin-top:.25rem;}
.analyze-btn:hover{opacity:.85;}.analyze-btn:active{transform:scale(.99);}
.analyze-btn:disabled{opacity:.35;cursor:not-allowed;}
.copy-btn{background:transparent;border:1px solid var(--border2);color:var(--muted);border-radius:9px;padding:8px 14px;font-size:12px;font-weight:500;cursor:pointer;font-family:'Epilogue',sans-serif;transition:background .12s,color .12s;}
.copy-btn:hover{background:var(--surface2);color:var(--text);}
.go-btn{display:inline-flex;align-items:center;gap:4px;background:var(--text);color:var(--bg);text-decoration:none;border-radius:9px;padding:8px 14px;font-size:12px;font-weight:500;font-family:'Epilogue',sans-serif;transition:opacity .12s;}
.go-btn:hover{opacity:.8;}
.btn-row{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-top:4px;}
.copied-msg{font-size:11px;color:var(--green);animation:fadeUp .2s ease;}
.post-btn{background:transparent;border:1px solid;border-radius:9px;padding:8px 14px;font-size:12px;font-weight:600;cursor:pointer;font-family:'Epilogue',sans-serif;transition:opacity .12s;}
.post-btn:hover{opacity:.7;}.post-btn:disabled{opacity:.5;cursor:not-allowed;}
.post-btn.success{text-decoration:none;display:inline-flex;align-items:center;}
.post-btn.posting{border-color:var(--border2);color:var(--muted);}
.post-btn.retry{border-color:var(--red);color:var(--red);margin-left:8px;}
.post-error{display:flex;align-items:center;font-size:12px;color:var(--red);}
.loading-wrap{margin-top:14px;}
.loading-bar{height:3px;background:var(--border);border-radius:99px;overflow:hidden;}
.loading-bar-fill{height:100%;background:var(--accent);border-radius:99px;animation:loadpulse 1.4s ease-in-out infinite;}
.loading-label{font-size:12px;color:var(--muted);margin-top:8px;text-align:center;}
@keyframes loadpulse{0%{width:5%;margin-left:0;}50%{width:40%;margin-left:30%;}100%{width:5%;margin-left:95%;}}
.result-card{background:var(--surface);border-radius:20px;padding:1.5rem;margin-bottom:1rem;border:1px solid var(--border);animation:fadeUp .35s ease;}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
.platform-badge{display:inline-flex;align-items:center;gap:9px;padding:5px 14px 5px 5px;border-radius:99px;font-size:13px;font-weight:600;margin-bottom:14px;}
.platform-icon{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#fff;}
.result-section{margin-bottom:1rem;}
.result-section-title{font-size:10px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);margin-bottom:6px;}
.result-text{font-size:14px;line-height:1.65;color:var(--text);white-space:pre-wrap;}
.result-reasoning{font-size:13px;color:var(--muted);line-height:1.65;background:var(--surface2);border-radius:10px;padding:11px 14px;}
.alt-platforms{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;}
.alt-pill{font-size:11px;font-weight:500;padding:4px 11px;border-radius:99px;background:var(--surface2);color:var(--muted);border:1px solid var(--border);}
.price-num{font-family:'Playfair Display',serif;font-size:1.7rem;color:var(--text);line-height:1;}
.price-note{font-size:12px;color:var(--muted);margin-top:4px;}
.divider{border:none;border-top:1px solid var(--border);margin:1rem 0;}
.result-images{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;margin-top:4px;}
.result-images img{height:90px;width:90px;object-fit:cover;border-radius:10px;flex-shrink:0;border:1px solid var(--border);}
.editable-field{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:9px 12px;font-family:'Epilogue',sans-serif;font-size:13px;color:var(--text);outline:none;transition:border-color .15s;line-height:1.5;}
.editable-field:focus{border-color:var(--accent);background:var(--surface);}
.editable-textarea{resize:vertical;min-height:80px;}
.details-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px;}
.detail-chip{background:var(--surface2);border-radius:8px;padding:8px 10px;}
.detail-chip-label{font-size:10px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin-bottom:3px;}
.ai-badge{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;background:var(--accent-l);color:var(--accent);padding:3px 8px;border-radius:99px;margin-left:6px;}

/* ── History cards ── */
.history-grid{display:flex;flex-direction:column;gap:10px;}
.history-card{background:var(--surface);border:1px solid var(--border);border-radius:16px;overflow:hidden;transition:border-color .15s,opacity .15s;cursor:pointer;}
.history-card:hover{border-color:var(--border2);}
.history-card-inner{display:flex;}
.history-img{width:80px;height:80px;object-fit:cover;flex-shrink:0;border-right:1px solid var(--border);}
.history-img-placeholder{width:80px;height:80px;flex-shrink:0;background:var(--surface2);border-right:1px solid var(--border);display:flex;align-items:center;justify-content:center;font-size:24px;color:var(--muted);}
.history-body{padding:12px 14px;flex:1;min-width:0;}
.history-top{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;}
.history-name{font-size:14px;font-weight:600;color:var(--text);}
.history-seller{font-size:11px;color:var(--muted);margin-top:2px;}
.history-meta{font-size:11px;color:var(--muted);margin-top:4px;}
.ebay-link{display:inline-block;margin-top:6px;font-size:11px;font-weight:600;color:#0064D2;text-decoration:none;}
.ebay-link:hover{text-decoration:underline;}
.platform-pill{font-size:10px;font-weight:600;padding:3px 9px;border-radius:99px;flex-shrink:0;white-space:nowrap;}
.status-badge{font-size:10px;font-weight:600;padding:3px 9px;border-radius:99px;flex-shrink:0;white-space:nowrap;}

/* ── History action bar ── */
.history-actions{padding:8px 12px;border-top:1px solid var(--border);display:flex;flex-wrap:wrap;gap:6px;align-items:center;justify-content:flex-end;}
.action-btn{font-size:11px;font-weight:500;padding:5px 11px;border-radius:8px;cursor:pointer;font-family:'Epilogue',sans-serif;border:1px solid var(--border);background:transparent;color:var(--muted);transition:all .12s;white-space:nowrap;}
.action-btn:hover{background:var(--surface2);color:var(--text);}
.action-btn.sold{border-color:var(--green);color:var(--green);}
.action-btn.sold:hover{background:var(--green-l);}
.action-btn.cancel{border-color:var(--accent);color:var(--accent);}
.action-btn.cancel:hover{background:var(--accent-l);}
.action-btn.available{border-color:var(--border2);color:var(--muted);}
.action-btn.available:hover{background:var(--surface2);}
.action-btn.delete{border-color:var(--red);color:var(--red);}
.action-btn.delete:hover{background:#FEF0F0;}
.confirm-row{display:flex;gap:6px;align-items:center;width:100%;}
.confirm-msg{font-size:11px;color:var(--red);flex:1;}
.confirm-yes{font-size:11px;font-weight:600;padding:5px 11px;border-radius:8px;cursor:pointer;font-family:'Epilogue',sans-serif;border:1px solid var(--red);background:var(--red);color:#fff;transition:opacity .12s;}
.confirm-yes:hover{opacity:.85;}
.confirm-no{font-size:11px;padding:5px 11px;border-radius:8px;cursor:pointer;font-family:'Epilogue',sans-serif;border:1px solid var(--border);background:transparent;color:var(--muted);}

/* ── Filters ── */
.filter-row{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:14px;}
.filter-chip{font-size:12px;font-weight:500;padding:5px 12px;border-radius:99px;background:var(--surface);border:1px solid var(--border);cursor:pointer;color:var(--muted);transition:all .12s;font-family:'Epilogue',sans-serif;}
.filter-chip.active{background:var(--text);color:var(--bg);border-color:var(--text);}

/* ── Misc ── */
.seller-tag{display:inline-flex;align-items:center;gap:5px;font-size:12px;color:var(--muted);margin-top:8px;}
.seller-dot{width:22px;height:22px;border-radius:50%;background:var(--accent-l);color:var(--accent);font-size:10px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;}
.empty{text-align:center;padding:3rem 1rem;color:var(--muted);}
.empty-icon{font-size:2.5rem;margin-bottom:10px;}
.empty-text{font-size:13px;line-height:1.6;}
.auth-toast{position:fixed;top:16px;left:50%;transform:translateX(-50%);background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:10px 18px;font-size:13px;font-weight:500;color:var(--text);box-shadow:0 4px 20px rgba(0,0,0,.12);z-index:100;animation:fadeUp .3s ease;}
.auth-toast.success{border-color:var(--green);color:var(--green);}
.auth-toast.error{border-color:var(--red);color:var(--red);}

/* ── Analytics ── */
.stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:10px;margin-bottom:1.5rem;}
.stat-card{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:14px 16px;}
.stat-val{font-family:'Playfair Display',serif;font-size:1.6rem;color:var(--text);line-height:1;margin-bottom:4px;}
.stat-label{font-size:11px;color:var(--muted);font-weight:500;}
.stat-sub{font-size:11px;color:var(--green);margin-top:2px;}
.stat-sub.warn{color:var(--accent);}
.platform-bar-row{display:flex;align-items:center;gap:10px;margin-bottom:8px;}
.platform-bar-label{font-size:12px;color:var(--text);min-width:90px;}
.platform-bar-track{flex:1;height:8px;background:var(--surface2);border-radius:99px;overflow:hidden;}
.platform-bar-fill{height:100%;border-radius:99px;transition:width .5s;}
.platform-bar-count{font-size:12px;color:var(--muted);min-width:24px;text-align:right;}
.seller-row{display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);}
.seller-row:last-child{border-bottom:none;}

/* ── Costs ── */
.cost-platform-grid{display:flex;flex-direction:column;gap:8px;margin-bottom:1.25rem;}
.cost-platform-card{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;}
.cost-platform-left{display:flex;align-items:center;gap:10px;}
.cost-platform-icon{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;flex-shrink:0;}
.cost-platform-name{font-size:13px;font-weight:600;color:var(--text);}
.cost-platform-fee{font-size:11px;color:var(--muted);}
.cost-shipping-table{width:100%;border-collapse:collapse;font-size:13px;}
.cost-shipping-table th{text-align:left;font-size:10px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);padding:6px 0;border-bottom:1px solid var(--border);}
.cost-shipping-table td{padding:8px 0;border-bottom:1px solid var(--border);color:var(--text);}
.cost-shipping-table tr:last-child td{border-bottom:none;}
.calc-card{background:var(--surface);border:1px solid var(--border);border-radius:20px;padding:1.5rem;margin-bottom:1.25rem;}
.calc-result-row{display:flex;justify-content:space-between;align-items:center;padding:7px 0;border-bottom:1px solid var(--border);}
.calc-result-row:last-child{border-bottom:none;}
.calc-result-label{font-size:13px;color:var(--muted);}
.calc-result-val{font-size:13px;font-weight:600;color:var(--text);}
.calc-result-val.profit{color:var(--green);}
.calc-result-val.loss{color:var(--red);}
.calc-select{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:10px 13px;font-family:'Epilogue',sans-serif;font-size:13px;color:var(--text);outline:none;appearance:none;cursor:pointer;}
.calc-select:focus{border-color:var(--accent);}

/* ── Seller dropdown / add member ── */
.inline-add{display:flex;gap:6px;margin-top:8px;}
.inline-add input{flex:1;}
.mini-btn{font-size:12px;font-weight:600;padding:8px 12px;border-radius:9px;cursor:pointer;font-family:'Epilogue',sans-serif;border:1px solid var(--border2);background:var(--surface2);color:var(--text);white-space:nowrap;transition:background .12s;}
.mini-btn:hover{background:var(--border);}
.link-btn{background:none;border:none;color:var(--accent);font-size:12px;font-weight:600;cursor:pointer;font-family:'Epilogue',sans-serif;padding:6px 0;}

/* ── Header stats + search + footer ── */
.header-stats{display:flex;gap:18px;margin-top:16px;flex-wrap:wrap;}
.header-stat{display:flex;flex-direction:column;}
.header-stat-val{font-family:'Playfair Display',serif;font-size:1.25rem;color:#F7F3EE;line-height:1;}
.header-stat-val.accent{color:var(--accent);}
.header-stat-label{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#7A6F64;margin-top:4px;}
.search-row{display:flex;gap:8px;margin-bottom:12px;align-items:center;}
.search-input{flex:1;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:9px 13px;font-family:'Epilogue',sans-serif;font-size:13px;color:var(--text);outline:none;}
.search-input:focus{border-color:var(--accent);background:var(--surface);}
.mini-btn:disabled{opacity:.4;cursor:not-allowed;}
.footer{max-width:740px;margin:2.5rem auto 0;padding:1.5rem 1.25rem;text-align:center;border-top:1px solid var(--border);}
.footer-brand{font-family:'Playfair Display',serif;font-style:italic;font-size:1rem;color:var(--muted);}
.footer-sub{font-size:11px;color:var(--muted);margin-top:4px;}

/* ── Modal ── */
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:flex-end;justify-content:center;z-index:200;animation:fadeUp .2s ease;padding:0;}
.modal{background:var(--surface);width:100%;max-width:740px;border-radius:22px 22px 0 0;padding:1.5rem 1.25rem 2rem;max-height:92vh;overflow-y:auto;}
@media(min-width:560px){.modal-overlay{align-items:center;}.modal{border-radius:22px;}}
.modal-title{font-family:'Playfair Display',serif;font-size:1.5rem;color:var(--text);margin-bottom:4px;}
.modal-sub{font-size:12px;color:var(--muted);margin-bottom:1.25rem;}
.modal-actions{display:flex;gap:8px;margin-top:1.25rem;}
.modal-actions .analyze-btn{margin-top:0;}
.split-row{display:flex;align-items:center;gap:8px;margin-bottom:8px;}
.split-row select{flex:2;}
.split-pct{width:74px;flex-shrink:0;}
.split-amt{font-size:13px;font-weight:600;color:var(--green);min-width:64px;text-align:right;}
.split-remove{background:none;border:none;color:var(--red);font-size:18px;cursor:pointer;line-height:1;padding:0 2px;}
.net-row{display:flex;justify-content:space-between;font-size:13px;padding:8px 0;border-top:1px solid var(--border);margin-top:6px;}
.net-row b{font-weight:600;}
.split-warn{font-size:11px;color:var(--accent);margin-top:4px;}

/* ── Payouts ── */
.payout-member{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:14px 16px;margin-bottom:10px;}
.payout-member-top{display:flex;justify-content:space-between;align-items:center;}
.payout-name{font-size:15px;font-weight:600;color:var(--text);}
.payout-venmo{font-size:11px;color:var(--muted);margin-top:2px;}
.payout-owed{font-family:'Playfair Display',serif;font-size:1.4rem;color:var(--accent);line-height:1;}
.payout-owed.zero{color:var(--muted);}
.payout-splitline{display:flex;justify-content:space-between;align-items:center;font-size:12px;padding:7px 0;border-top:1px solid var(--border);}
.payout-splitline .pid{color:var(--muted);}
.pay-btn{font-size:11px;font-weight:600;padding:4px 10px;border-radius:7px;cursor:pointer;font-family:'Epilogue',sans-serif;border:1px solid var(--green);background:transparent;color:var(--green);}
.pay-btn:hover{background:var(--green-l);}
.pay-btn.paid{border-color:var(--border);color:var(--muted);background:var(--surface2);cursor:default;}

@media(max-width:480px){.row3{grid-template-columns:1fr 1fr;}.header{padding:1.5rem 1.25rem 2.5rem;}.details-grid{grid-template-columns:1fr;}}
`;

// ── History card action bar ────────────────────────────────────────────────────
function HistoryActions({ item, onStatusChange, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const status = item.status || (item.sold ? "sold" : "available");

  return (
    <div className="history-actions" onClick={e => e.stopPropagation()}>
      {confirmDelete ? (
        <div className="confirm-row">
          <span className="confirm-msg">Permanently delete this listing?</span>
          <button className="confirm-no" onClick={() => setConfirmDelete(false)}>No</button>
          <button className="confirm-yes" onClick={() => { setConfirmDelete(false); onDelete(item.id); }}>Yes, delete</button>
        </div>
      ) : (
        <>
          {/* Mark as Sold — shown when available or cancelled */}
          {status !== "sold" && (
            <button className="action-btn sold" onClick={() => onStatusChange(item.id, "sold")}>
              Mark sold
            </button>
          )}
          {/* Cancel — shown when available */}
          {status === "available" && (
            <button className="action-btn cancel" onClick={() => onStatusChange(item.id, "cancelled")}>
              Cancel listing
            </button>
          )}
          {/* Make Available — shown when sold or cancelled */}
          {(status === "sold" || status === "cancelled") && (
            <button className="action-btn available" onClick={() => onStatusChange(item.id, "available")}>
              Make available
            </button>
          )}
          {/* Delete — always shown */}
          <button className="action-btn delete" onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        </>
      )}
    </div>
  );
}

// ── Analytics Dashboard ───────────────────────────────────────────────────────
function AnalyticsDashboard({ history }) {
  const active = history.filter(i => !["cancelled"].includes(i.status || (i.sold ? "sold" : "available")));
  const total = active.length;
  const sold = history.filter(i => (i.status || (i.sold ? "sold" : "available")) === "sold").length;
  const cancelled = history.filter(i => (i.status || (i.sold ? "sold" : "available")) === "cancelled").length;
  const available = history.filter(i => (i.status || (i.sold ? "sold" : "available")) === "available").length;
  const soldItems = history.filter(i => (i.status || (i.sold ? "sold" : "available")) === "sold");
  const revenue = soldItems.reduce((sum, i) => sum + (i.sale_price != null ? Number(i.sale_price) : parsePrice(i.suggested_price)), 0);
  const profit = soldItems.reduce((sum, i) => sum + listingProfit(i), 0);
  const avgPrice = sold > 0 ? (revenue / sold).toFixed(2) : "0.00";
  const soldRate = total > 0 ? Math.round((sold / total) * 100) : 0;

  const platformCounts = {};
  history.forEach(i => {
    if (i.platform_key) platformCounts[i.platform_key] = (platformCounts[i.platform_key] || 0) + 1;
  });
  const topPlatforms = Object.entries(platformCounts).sort((a, b) => b[1] - a[1]);
  const grandTotal = history.length;

  const sellerStats = {};
  history.forEach(i => {
    if (!i.seller_name) return;
    const st = i.status || (i.sold ? "sold" : "available");
    if (!sellerStats[i.seller_name]) sellerStats[i.seller_name] = { listed: 0, sold: 0, cancelled: 0, revenue: 0, profit: 0 };
    sellerStats[i.seller_name].listed++;
    if (st === "sold") {
      sellerStats[i.seller_name].sold++;
      sellerStats[i.seller_name].revenue += i.sale_price != null ? Number(i.sale_price) : parsePrice(i.suggested_price);
      sellerStats[i.seller_name].profit += listingProfit(i);
    }
    if (st === "cancelled") sellerStats[i.seller_name].cancelled++;
  });
  const sellers = Object.entries(sellerStats).sort((a, b) => b[1].listed - a[1].listed);
  const recentSold = history.filter(i => (i.status || (i.sold ? "sold" : "available")) === "sold").slice(0, 5);

  return (
    <div>
      <div className="section-label">Overview</div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-val">{grandTotal}</div>
          <div className="stat-label">Total created</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{ color: "var(--green)" }}>{sold}</div>
          <div className="stat-label">Sold</div>
          <div className="stat-sub">{soldRate}% sell-through</div>
        </div>
        <div className="stat-card">
          <div className="stat-val">{available}</div>
          <div className="stat-label">Available</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{ color: "var(--accent)" }}>{cancelled}</div>
          <div className="stat-label">Cancelled</div>
          {cancelled > 0 && <div className="stat-sub warn">{Math.round((cancelled/grandTotal)*100)}% of listings</div>}
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{ fontSize: "1.3rem" }}>${revenue.toFixed(2)}</div>
          <div className="stat-label">Revenue</div>
          <div className="stat-sub">avg ${avgPrice}/sale</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{ fontSize: "1.3rem", color: profit >= 0 ? "var(--green)" : "var(--red)" }}>${profit.toFixed(2)}</div>
          <div className="stat-label">Net profit</div>
          <div className="stat-sub">after fees, shipping &amp; cost</div>
        </div>
      </div>

      {topPlatforms.length > 0 && (
        <>
          <div className="section-label" style={{ marginTop: 8 }}>Listings by platform</div>
          <div className="form-card" style={{ paddingBottom: "1rem" }}>
            {topPlatforms.map(([key, count]) => {
              const p = PLATFORMS[key];
              if (!p) return null;
              return (
                <div key={key} className="platform-bar-row">
                  <div className="platform-bar-label" style={{ color: p.color, fontWeight: 600 }}>{p.name}</div>
                  <div className="platform-bar-track">
                    <div className="platform-bar-fill" style={{ width: `${Math.round((count / grandTotal) * 100)}%`, background: p.color }} />
                  </div>
                  <div className="platform-bar-count">{count}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {sellers.length > 0 && (
        <>
          <div className="section-label" style={{ marginTop: 8 }}>By seller</div>
          <div className="form-card">
            {sellers.map(([name, s]) => (
              <div key={name} className="seller-row">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="seller-dot">{initials(name)}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>
                      {s.listed} listed · {s.sold} sold
                      {s.cancelled > 0 && ` · ${s.cancelled} cancelled`}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--green)" }}>${s.revenue.toFixed(2)}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>rev · ${s.profit.toFixed(2)} profit</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {recentSold.length > 0 && (
        <>
          <div className="section-label" style={{ marginTop: 8 }}>Recently sold</div>
          <div className="history-grid">
            {recentSold.map(item => {
              const p = item.platform_key ? PLATFORMS[item.platform_key] : null;
              return (
                <div key={item.id} className="history-card" style={{ cursor: "default", opacity: 0.7 }}>
                  <div className="history-card-inner">
                    {item.image_urls?.[0] ? <img className="history-img" src={item.image_urls[0]} alt={item.item_name} /> : <div className="history-img-placeholder">👕</div>}
                    <div className="history-body">
                      <div className="history-top">
                        <div>
                          <div className="history-name">{item.item_name}</div>
                          <div className="history-seller">{item.seller_name}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--green)" }}>{item.suggested_price}</div>
                          {p && <span className="platform-pill" style={{ background: p.color + "18", color: p.color }}>{p.name}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {history.length === 0 && (
        <div className="empty"><div className="empty-icon">📊</div><div className="empty-text">No data yet. List your first item to start tracking analytics.</div></div>
      )}
    </div>
  );
}

// ── Costs & Calculator ────────────────────────────────────────────────────────
function CostsCalculator() {
  const [salePrice, setSalePrice] = useState("");
  const [itemCost, setItemCost] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("ebay");
  const [selectedShipping, setSelectedShipping] = useState(0);

  const sale = parseFloat(salePrice) || 0;
  const cost = parseFloat(itemCost) || 0;
  const p = PLATFORMS[selectedPlatform];
  const platformFee = p ? (sale * p.feeRate + (p.flatFee || 0)) : 0;
  const shippingCost = SHIPPING[selectedShipping]?.cost || 0;
  const totalCosts = platformFee + shippingCost + cost;
  const profit = sale - totalCosts;

  return (
    <div>
      <div className="section-label">Platform selling fees</div>
      <div className="cost-platform-grid">
        {Object.entries(PLATFORMS).map(([key, p]) => (
          <div key={key} className="cost-platform-card">
            <div className="cost-platform-left">
              <div className="cost-platform-icon" style={{ background: p.color }}>{p.icon}</div>
              <div>
                <div className="cost-platform-name">{p.name}</div>
                <div className="cost-platform-fee">
                  {key === "etsy" && "5% transaction + 3% payment + $0.20 listing"}
                  {key === "ebay" && "~13% final value fee (varies by category)"}
                  {key === "depop" && "10% on sale price"}
                  {key === "mercari" && "0% seller fee · buyer pays service fee"}
                  {key === "grailed" && "9% + payment processing ~3%"}
                  {key === "facebook" && "Free for local pickup · 5% for shipping orders"}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: p.color, flexShrink: 0 }}>{p.fee}</div>
          </div>
        ))}
      </div>

      <div className="section-label" style={{ marginTop: 8 }}>Shipping rates (approx.)</div>
      <div className="form-card" style={{ padding: "0 1.5rem" }}>
        <table className="cost-shipping-table">
          <thead><tr><th>Service</th><th style={{ textAlign: "right" }}>Est. Cost</th></tr></thead>
          <tbody>
            {SHIPPING.map((s, i) => (
              <tr key={i}><td>{s.name}</td><td style={{ textAlign: "right", fontWeight: 600 }}>{s.cost === 0 ? "Free" : `$${s.cost.toFixed(2)}`}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section-label" style={{ marginTop: 8 }}>Profit calculator</div>
      <div className="calc-card">
        <div className="row2" style={{ marginBottom: 10 }}>
          <div className="field">
            <label>Sale price ($)</label>
            <input type="number" placeholder="0.00" value={salePrice} onChange={e => setSalePrice(e.target.value)} min="0" step="0.01" />
          </div>
          <div className="field">
            <label>Your item cost ($)</label>
            <input type="number" placeholder="0.00" value={itemCost} onChange={e => setItemCost(e.target.value)} min="0" step="0.01" />
          </div>
        </div>
        <div className="row2" style={{ marginBottom: 16 }}>
          <div className="field">
            <label>Selling platform</label>
            <select className="calc-select" value={selectedPlatform} onChange={e => setSelectedPlatform(e.target.value)}>
              {Object.entries(PLATFORMS).map(([key, p]) => <option key={key} value={key}>{p.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Shipping service</label>
            <select className="calc-select" value={selectedShipping} onChange={e => setSelectedShipping(Number(e.target.value))}>
              {SHIPPING.map((s, i) => <option key={i} value={i}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "12px 14px" }}>
          <div className="calc-result-row"><span className="calc-result-label">Sale price</span><span className="calc-result-val">${sale.toFixed(2)}</span></div>
          <div className="calc-result-row"><span className="calc-result-label">Item cost</span><span className="calc-result-val">− ${cost.toFixed(2)}</span></div>
          <div className="calc-result-row"><span className="calc-result-label">{p?.name} fee</span><span className="calc-result-val">− ${platformFee.toFixed(2)}</span></div>
          <div className="calc-result-row"><span className="calc-result-label">Shipping</span><span className="calc-result-val">− ${shippingCost.toFixed(2)}</span></div>
          <div className="calc-result-row" style={{ marginTop: 4, paddingTop: 10, borderTop: "2px solid var(--border)" }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Your profit</span>
            <span className={`calc-result-val ${profit >= 0 ? "profit" : "loss"}`} style={{ fontSize: 16 }}>
              {profit >= 0 ? "+" : ""}${profit.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sold modal: capture actual sale price + split proceeds ──────────────────────
function SoldModal({ item, members, onClose, onConfirm }) {
  const p = item.platform_key ? PLATFORMS[item.platform_key] : null;
  const [salePrice, setSalePrice] = useState(
    item.sale_price != null ? String(item.sale_price) : String(parsePrice(item.suggested_price) || "")
  );
  const [feeManual, setFeeManual] = useState(false);
  const [fee, setFee] = useState("");
  const [shipping, setShipping] = useState(item.shipping_cost ? String(item.shipping_cost) : "0");

  // default split: 100% to the item's seller (owner)
  const ownerId = item.seller_id ||
    members.find(m => m.name === item.seller_name)?.id ||
    members[0]?.id || "";
  const [splits, setSplits] = useState([{ member_id: ownerId, pct: "100" }]);
  const [saving, setSaving] = useState(false);

  const sale = parseFloat(salePrice) || 0;
  const autoFee = p ? +(sale * p.feeRate + (p.flatFee || 0)).toFixed(2) : 0;
  const feeVal = feeManual ? (parseFloat(fee) || 0) : autoFee;
  const ship = parseFloat(shipping) || 0;
  const net = Math.max(0, sale - feeVal - ship);
  const pctSum = splits.reduce((s, r) => s + (parseFloat(r.pct) || 0), 0);
  const pctOk = Math.abs(pctSum - 100) < 0.01;

  const setSplit = (i, k, v) => setSplits(s => s.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addSplit = () => setSplits(s => [...s, { member_id: members[0]?.id || "", pct: "0" }]);
  const removeSplit = (i) => setSplits(s => s.filter((_, idx) => idx !== i));

  const confirm = async () => {
    if (sale <= 0 || !pctOk) return;
    setSaving(true);
    const rows = splits
      .filter(r => r.member_id)
      .map(r => {
        const m = members.find(x => x.id === r.member_id);
        const pct = parseFloat(r.pct) || 0;
        return {
          member_id: r.member_id,
          member_name: m?.name || "",
          split_pct: pct,
          amount: +(net * pct / 100).toFixed(2),
        };
      });
    await onConfirm({ sale_price: sale, platform_fee: feeVal, shipping_cost: ship, splits: rows });
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">Mark as sold</div>
        <div className="modal-sub">{item.item_name} · listed by {item.seller_name || "—"}</div>

        <div className="row2">
          <div className="field">
            <label>Actual sale price ($) *</label>
            <input type="number" min="0" step="0.01" value={salePrice} onChange={e => setSalePrice(e.target.value)} autoFocus />
          </div>
          <div className="field">
            <label>Shipping you paid ($)</label>
            <input type="number" min="0" step="0.01" value={shipping} onChange={e => setShipping(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label>{p ? `${p.name} fee` : "Platform fee"} ($)
            {!feeManual && p && <span style={{ color: "var(--muted)", fontWeight: 400 }}> · auto {p.fee}</span>}
          </label>
          <input
            type="number" min="0" step="0.01"
            value={feeManual ? fee : autoFee.toFixed(2)}
            onChange={e => { setFeeManual(true); setFee(e.target.value); }}
          />
          {feeManual && <button className="link-btn" onClick={() => setFeeManual(false)}>↺ Use auto fee</button>}
        </div>

        <div className="net-row"><span>Net proceeds to split</span><b>${net.toFixed(2)}</b></div>

        <div className="section-label" style={{ marginTop: 14 }}>Payout split</div>
        {splits.map((r, i) => {
          const amt = net * (parseFloat(r.pct) || 0) / 100;
          return (
            <div key={i} className="split-row">
              <select value={r.member_id} onChange={e => setSplit(i, "member_id", e.target.value)}>
                <option value="">Select member…</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <input className="split-pct" type="number" min="0" max="100" step="1" value={r.pct}
                onChange={e => setSplit(i, "pct", e.target.value)} />
              <span style={{ fontSize: 12, color: "var(--muted)" }}>%</span>
              <span className="split-amt">${amt.toFixed(2)}</span>
              {splits.length > 1 && <button className="split-remove" onClick={() => removeSplit(i)}>×</button>}
            </div>
          );
        })}
        {members.length > splits.length && (
          <button className="link-btn" onClick={addSplit}>+ Add another member to split</button>
        )}
        {!pctOk && <div className="split-warn">Splits add up to {pctSum}% — they must total 100%.</div>}

        <div className="modal-actions">
          <button className="copy-btn" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="analyze-btn" style={{ flex: 2 }} disabled={sale <= 0 || !pctOk || saving} onClick={confirm}>
            {saving ? "Saving…" : `Confirm sale · $${net.toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Payouts dashboard ───────────────────────────────────────────────────────────
function PayoutsDashboard({ earnings, splits, listings, onMarkPaid, onSettleMember, onAddMember }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newVenmo, setNewVenmo] = useState("");

  const listingName = (id) => listings.find(l => l.id === id)?.item_name || "item";
  const unpaid = splits.filter(s => !s.paid).sort((a, b) => b.amount - a.amount);
  const totalUnpaid = unpaid.reduce((s, r) => s + Number(r.amount || 0), 0);

  const submitAdd = async () => {
    if (!newName.trim()) return;
    await onAddMember({ name: newName.trim(), venmo_handle: newVenmo.trim() });
    setNewName(""); setNewVenmo(""); setShowAdd(false);
  };

  return (
    <div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-val" style={{ color: "var(--accent)" }}>${totalUnpaid.toFixed(2)}</div>
          <div className="stat-label">Owed to members</div>
        </div>
        <div className="stat-card">
          <div className="stat-val">{earnings.length}</div>
          <div className="stat-label">Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-val" style={{ color: "var(--green)" }}>{splits.filter(s => s.paid).length}</div>
          <div className="stat-label">Payouts settled</div>
        </div>
      </div>

      <div className="section-label" style={{ marginTop: 8 }}>Members</div>
      {earnings.length === 0 && <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 10 }}>No members yet.</div>}
      {earnings.map(m => (
        <div key={m.member_id} className="payout-member">
          <div className="payout-member-top">
            <div>
              <div className="payout-name">{m.name}</div>
              <div className="payout-venmo">
                {m.venmo_handle ? m.venmo_handle : "no Venmo on file"} · {m.items_sold} sold / {m.items_listed} listed
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className={`payout-owed ${Number(m.unpaid_owed) === 0 ? "zero" : ""}`}>${Number(m.unpaid_owed).toFixed(2)}</div>
              <div style={{ fontSize: 10, color: "var(--muted)" }}>unpaid</div>
            </div>
          </div>
          {Number(m.unpaid_owed) > 0 && (
            <button className="pay-btn" style={{ marginTop: 10 }} onClick={() => onSettleMember(m.member_id, m.name)}>
              Settle up · ${Number(m.unpaid_owed).toFixed(2)}
            </button>
          )}
        </div>
      ))}
      {showAdd ? (
        <div className="form-card" style={{ marginTop: 4 }}>
          <div className="field"><label>Name *</label><input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Jamie" /></div>
          <div className="field"><label>Venmo handle</label><input type="text" value={newVenmo} onChange={e => setNewVenmo(e.target.value)} placeholder="@jamie-resells" /></div>
          <div className="inline-add">
            <button className="copy-btn" style={{ flex: 1 }} onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="mini-btn" style={{ flex: 1 }} onClick={submitAdd}>Add member</button>
          </div>
        </div>
      ) : (
        <button className="link-btn" onClick={() => setShowAdd(true)}>+ Add member</button>
      )}

      <div className="section-label" style={{ marginTop: 16 }}>Outstanding payouts</div>
      {unpaid.length === 0 ? (
        <div className="empty" style={{ padding: "2rem 1rem" }}><div className="empty-icon">✅</div><div className="empty-text">All settled up.</div></div>
      ) : (
        unpaid.map(s => (
          <div key={s.id} className="payout-splitline">
            <span><b>{s.member_name}</b> <span className="pid">· {listingName(s.listing_id)}{s.split_pct != null ? ` · ${s.split_pct}%` : ""}</span></span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600 }}>${Number(s.amount).toFixed(2)}</span>
              <button className="pay-btn" onClick={() => onMarkPaid(s.id, true)}>Mark paid</button>
            </span>
          </div>
        ))
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("cc-theme") || "light");
  const [tab, setTab] = useState("list");
  const [historySubTab, setHistorySubTab] = useState("items");
  const [form, setForm] = useState(BLANK);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState(0);
  const [result, setResult] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editHashtags, setEditHashtags] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [connectionStatus, setConnectionStatus] = useState({ etsy: false, ebay: false });
  const [authToast, setAuthToast] = useState(null);
  const [members, setMembers] = useState([]);
  const [payoutSplits, setPayoutSplits] = useState([]);
  const [memberEarnings, setMemberEarnings] = useState([]);
  const [soldItem, setSoldItem] = useState(null);
  const [currentListingId, setCurrentListingId] = useState(null);
  const [addingSeller, setAddingSeller] = useState(false);
  const [newSellerName, setNewSellerName] = useState("");
  const resultRef = useRef(null);
  const loadTimer = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("cc-theme", theme);
  }, [theme]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const etsyAuth = params.get("etsy_auth");
    const ebayAuth = params.get("ebay_auth");
    if (etsyAuth || ebayAuth) {
      const platform = etsyAuth ? "Etsy" : "eBay";
      const success = (etsyAuth || ebayAuth) === "success";
      setAuthToast({ msg: success ? `${platform} connected!` : `${platform} connection failed`, type: success ? "success" : "error" });
      setTimeout(() => setAuthToast(null), 4000);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const refreshStatus = async () => { const s = await getConnectionStatus(); setConnectionStatus(s); };
  useEffect(() => { refreshStatus(); }, []);

  useEffect(() => {
    (async () => {
      setHistoryLoading(true);
      try {
        const rows = await fetchListings();
        if (rows?.length) setHistory(rows.map(normaliseItem));
      } catch {}
      setHistoryLoading(false);
    })();
  }, []);

  const refreshMembers = async () => { setMembers(await fetchMembers()); };
  const refreshPayouts = async () => {
    const [sp, ea] = await Promise.all([fetchPayoutSplits(), fetchMemberEarnings()]);
    setPayoutSplits(sp); setMemberEarnings(ea);
  };
  useEffect(() => { refreshMembers(); refreshPayouts(); }, []);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectSeller = (id) => {
    const m = members.find(x => x.id === id);
    setForm(f => ({ ...f, sellerId: id, sellerName: m?.name || "" }));
  };

  const handleAddSeller = async () => {
    if (!newSellerName.trim()) return;
    const m = await addMember({ name: newSellerName.trim() });
    setNewSellerName(""); setAddingSeller(false);
    await refreshMembers();
    if (m) setForm(f => ({ ...f, sellerId: m.id, sellerName: m.name }));
  };

  const analyzeImages = async (uploadedImages) => {
    if (!uploadedImages.length) return;
    setAiImageLoading(true);
    try {
      const imageContents = uploadedImages.slice(0, 3).map(url => ({ type: "image", source: { type: "url", url } }));
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_ANTHROPIC_API_KEY,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 500,
          messages: [{ role: "user", content: [
            ...imageContents,
            { type: "text", text: `Look at this clothing item and extract what you can see. Respond ONLY with JSON, no markdown:
{"itemName":"short descriptive name","brand":"brand name or empty string","color":"main color","material":"fabric if visible or empty string","category":"one of: Jacket / Coat, Shirt / Top, Pants / Jeans, Dress / Skirt, Sweater / Knitwear, Suit / Blazer, Shoes / Footwear, Accessories, Sportswear, Vintage / Collectible, Other","gender":"Men's or Women's or Unisex or empty string","condition":"Excellent / Like new or Good — minor wear or Fair — visible wear or Distressed / As-is or empty string"}` }
          ]}],
        }),
      });
      const data = await res.json();
      const text = data.content?.find(b => b.type === "text")?.text || "";
      const parsed = JSON.parse(text.replace(/```json|```/g, "").trim());
      setForm(f => ({
        ...f,
        itemName: parsed.itemName || f.itemName,
        brand: parsed.brand || f.brand,
        color: parsed.color || f.color,
        material: parsed.material || f.material,
        category: parsed.category || f.category,
        gender: parsed.gender || f.gender,
        condition: parsed.condition || f.condition,
      }));
    } catch {}
    setAiImageLoading(false);
  };

  const handleImagesChange = (newImages) => {
    const prevLen = images.length;
    setImages(newImages);
    if (newImages.length > prevLen) analyzeImages(newImages);
  };

  const analyze = async () => {
    if (!form.itemName || !form.category || !form.condition || !form.sellerName) return;
    setLoading(true); setResult(null); setLoadMsg(0);
    loadTimer.current = setInterval(() => setLoadMsg(m => Math.min(m + 1, LOAD_MSGS.length - 1)), 1800);

    const prompt = `You are an expert vintage and secondhand clothing reseller. Analyze this item and recommend the single best platform from: Depop, Mercari, Etsy, Grailed, eBay, or Facebook Marketplace.
Item:
- Name: ${form.itemName}
- Category: ${form.category}
- Gender: ${form.gender || "unspecified"}
- Brand: ${form.brand || "unbranded"}
- Era: ${form.era || "unknown"}
- Size: ${form.size || "not specified"}
- Condition: ${form.condition}
- Material: ${form.material || "not specified"}
- Color: ${form.color || "not specified"}
- Notes: ${form.description || "none"}
- Asking price: ${form.askingPrice || "not specified"}
Respond ONLY with valid JSON, no markdown:
{"platform":"Depop","platformKey":"depop","reasoning":"2-3 sentences","alternativePlatforms":["Mercari","eBay"],"suggestedTitle":"title under 80 chars","suggestedDescription":"80-150 word description","suggestedPrice":"$XX","pricingNote":"one sentence","hashtags":"#tag1 #tag2 #tag3 #tag4 #tag5 #tag6"}`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_ANTHROPIC_API_KEY,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, messages: [{ role: "user", content: prompt }] }),
      });
      const data = await res.json();
      const text = data.content?.find(b => b.type === "text")?.text || "";
      const parsed = JSON.parse(text.replace(/```json|```/g, "").trim());
      setResult(parsed);
      setEditTitle(parsed.suggestedTitle || "");
      setEditDesc(parsed.suggestedDescription || "");
      setEditPrice(parsed.suggestedPrice || "");
      setEditHashtags(parsed.hashtags || "");

      const row = {
        seller_name: form.sellerName, seller_id: form.sellerId || null,
        item_name: form.itemName, category: form.category,
        gender: form.gender, brand: form.brand, era: form.era, size: form.size,
        condition: form.condition, material: form.material, color: form.color,
        description: form.description, asking_price: form.askingPrice, image_urls: images,
        platform: parsed.platform, platform_key: parsed.platformKey,
        suggested_price: parsed.suggestedPrice, suggested_title: parsed.suggestedTitle,
        suggested_description: parsed.suggestedDescription, hashtags: parsed.hashtags,
        reasoning: parsed.reasoning, alternative_platforms: parsed.alternativePlatforms,
        pricing_note: parsed.pricingNote, sold: false, status: "available",
        item_cost: parsePrice(form.itemCost),
      };
      const saved = await saveListing(row);
      setCurrentListingId(saved?.id || null);
      setHistory(h => [normaliseItem({ id: saved?.id || Date.now(), created_at: new Date().toISOString(), ...row }), ...h]);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      setResult({ error: "Something went wrong. Check your API keys and try again." });
    }
    clearInterval(loadTimer.current); setLoading(false);
  };

  const copyListing = () => {
    if (!result) return;
    navigator.clipboard.writeText(`${editTitle}\n\n${editDesc}\n\n${editHashtags}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => { setForm(BLANK); setResult(null); setImages([]); setEditTitle(""); setEditDesc(""); setEditPrice(""); setEditHashtags(""); };

  const handleStatusChange = async (id, newStatus) => {
    if (newStatus === "sold") {
      // open the sold modal to capture sale price + payout split
      const item = history.find(i => i.id === id);
      if (item) { setSoldItem(item); return; }
    }
    // moving away from sold → clear any recorded payout splits
    const prev = history.find(i => i.id === id);
    if (prev && (prev.status === "sold") && newStatus !== "sold") {
      await clearPayoutSplits(id);
    }
    await updateStatus(id, newStatus);
    setHistory(h => h.map(i => i.id === id ? { ...i, status: newStatus, sold: newStatus === "sold" } : i));
    refreshPayouts();
  };

  const flash = (msg, type = "success") => {
    setAuthToast({ msg, type });
    setTimeout(() => setAuthToast(null), 3000);
  };

  const confirmSale = async ({ sale_price, platform_fee, shipping_cost, splits }) => {
    const id = soldItem.id;
    await recordSale(id, { sale_price, platform_fee, shipping_cost });
    await clearPayoutSplits(id);
    if (splits.length) {
      await savePayoutSplits(splits.map(s => ({ ...s, listing_id: id })));
    }
    setHistory(h => h.map(i => i.id === id
      ? { ...i, status: "sold", sold: true, sale_price, platform_fee, shipping_cost }
      : i));
    setSoldItem(null);
    refreshPayouts();
    flash(`Sale recorded · $${sale_price.toFixed(2)}`);
  };

  const handlePosted = async (platformKey, res) => {
    if (platformKey !== "ebay" || !res?.listingUrl || !currentListingId) return;
    await updateListing(currentListingId, { ebay_url: res.listingUrl });
    setHistory(h => h.map(i => i.id === currentListingId ? { ...i, ebay_url: res.listingUrl } : i));
    flash("Saved eBay link to this item");
  };

  const handleMarkPaid = async (splitId, paid) => {
    await markSplitPaid(splitId, paid);
    refreshPayouts();
    if (paid) flash("Payout marked paid");
  };

  const handleSettleMember = async (memberId, name) => {
    await markMemberPaid(memberId);
    refreshPayouts();
    flash(`Settled up with ${name}`);
  };

  const handleDelete = async (id) => {
    await deleteListing(id);
    setHistory(h => h.filter(i => i.id !== id));
    refreshPayouts();
  };

  const platform = result?.platformKey ? PLATFORMS[result.platformKey] : null;
  const ebayPlatform = PLATFORMS.ebay;

  const statusOf = (i) => i.status || (i.sold ? "sold" : "available");
  const availableCount = history.filter(i => statusOf(i) === "available").length;
  const soldCount = history.filter(i => statusOf(i) === "sold").length;
  const totalOwed = payoutSplits.filter(s => !s.paid).reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const filteredHistory = history.filter(i => {
    const st = i.status || (i.sold ? "sold" : "available");
    if (filter !== "all" && st !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = [i.item_name, i.seller_name, i.brand, i.category, i.platform].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const exportCSV = () => {
    const cols = ["item_name","seller_name","category","brand","size","condition","status","asking_price","item_cost","sale_price","platform_fee","shipping_cost","platform","ebay_url","created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = history.map(i => cols.map(c => esc(i[c])).join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `closet-collective-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildPayload = () => ({
    ...form, imageUrls: images,
    suggestedTitle: editTitle, suggestedDescription: editDesc,
    suggestedPrice: editPrice, hashtags: editHashtags,
    reasoning: result?.reasoning, alternativePlatforms: result?.alternativePlatforms,
    pricingNote: result?.pricingNote,
  });

  return (
    <>
      <style>{STYLE}</style>
      {authToast && <div className={`auth-toast ${authToast.type}`}>{authToast.msg}</div>}
      <div className="app">
        <div className="header">
          <div className="header-top">
            <div>
              <div className="header-eyebrow">Closet Collective</div>
              <div className="header-title">List <em>smarter,</em><br/>sell faster.</div>
              <div className="header-sub">AI-powered platform matching + auto-posting</div>
            </div>
            <button className="theme-btn" onClick={() => setTheme(t => t === "light" ? "dark" : "light")}>
              {theme === "light" ? "◑" : "○"}
            </button>
          </div>
          <div className="header-stats">
            <div className="header-stat">
              <div className="header-stat-val">{availableCount}</div>
              <div className="header-stat-label">Available</div>
            </div>
            <div className="header-stat">
              <div className="header-stat-val">{soldCount}</div>
              <div className="header-stat-label">Sold</div>
            </div>
            <div className="header-stat">
              <div className="header-stat-val accent">${totalOwed.toFixed(0)}</div>
              <div className="header-stat-label">Owed out</div>
            </div>
          </div>
          <div className="header-wave" />
        </div>

        <div className="main">
          <div className="tab-row">
            <button className={`tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>New Listing</button>
            <button className={`tab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}>History ({history.length})</button>
            <button className={`tab ${tab === "payouts" ? "active" : ""}`} onClick={() => { setTab("payouts"); refreshPayouts(); }}>Payouts</button>
            <button className={`tab ${tab === "costs" ? "active" : ""}`} onClick={() => setTab("costs")}>Costs</button>
            <button className={`tab ${tab === "connect" ? "active" : ""}`} onClick={() => { setTab("connect"); refreshStatus(); }}>
              Platforms {(connectionStatus.etsy || connectionStatus.ebay) ? "●" : "○"}
            </button>
          </div>

          {/* ── CONNECT ── */}
          {tab === "connect" && (
            <>
              <div className="section-label">Connected platforms</div>
              <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16, lineHeight: 1.6 }}>
                Connect your Etsy and eBay accounts once. The app will auto-post listings directly.
              </p>
              <PlatformStatus status={connectionStatus} onDisconnect={refreshStatus} />
              <div style={{ marginTop: 20, padding: "14px 16px", background: "var(--surface)", borderRadius: 14, border: "1px solid var(--border)" }}>
                <div className="section-label" style={{ marginBottom: 6 }}>Other platforms</div>
                <p style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
                  Depop, Mercari, Grailed, and Facebook Marketplace don't offer public APIs. The app generates your copy-paste listing and opens the platform for you.
                </p>
              </div>
            </>
          )}

          {/* ── PAYOUTS ── */}
          {tab === "payouts" && (
            <PayoutsDashboard
              earnings={memberEarnings}
              splits={payoutSplits}
              listings={history}
              onMarkPaid={handleMarkPaid}
              onSettleMember={handleSettleMember}
              onAddMember={async (m) => { await addMember(m); await refreshMembers(); await refreshPayouts(); }}
            />
          )}

          {/* ── COSTS ── */}
          {tab === "costs" && <CostsCalculator />}

          {/* ── NEW LISTING ── */}
          {tab === "list" && (
            <>
              <div className="section-label">Who's listing?</div>
              <div className="form-card">
                <div className="field">
                  <label>Seller *</label>
                  {addingSeller ? (
                    <div className="inline-add">
                      <input type="text" placeholder="New seller name" value={newSellerName}
                        onChange={e => setNewSellerName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleAddSeller()} autoFocus />
                      <button className="mini-btn" onClick={handleAddSeller}>Add</button>
                      <button className="mini-btn" onClick={() => { setAddingSeller(false); setNewSellerName(""); }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <select value={form.sellerId} onChange={e => selectSeller(e.target.value)}>
                        <option value="">Select seller…</option>
                        {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <button className="link-btn" onClick={() => setAddingSeller(true)}>+ Add a new seller</button>
                    </>
                  )}
                </div>
              </div>

              <div className="section-label">
                Item photos
                {aiImageLoading && <span className="ai-badge">✦ AI reading photos…</span>}
              </div>
              <div className="form-card">
                <ImageUploader images={images} onChange={handleImagesChange} />
                {aiImageLoading && <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>Analyzing photos to pre-fill item details…</div>}
              </div>

              <div className="section-label">Item details</div>
              <div className="form-card">
                <div className="field">
                  <label>Item name / short description *</label>
                  <input type="text" placeholder="e.g. 90s Levi's 501 denim jacket" value={form.itemName} onChange={e => update("itemName", e.target.value)} />
                </div>
                <div className="row2">
                  <div className="field">
                    <label>Category *</label>
                    <select value={form.category} onChange={e => update("category", e.target.value)}>
                      <option value="">Select…</option>
                      <option>Jacket / Coat</option><option>Shirt / Top</option>
                      <option>Pants / Jeans</option><option>Dress / Skirt</option>
                      <option>Sweater / Knitwear</option><option>Suit / Blazer</option>
                      <option>Shoes / Footwear</option><option>Accessories</option>
                      <option>Sportswear</option><option>Vintage / Collectible</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Gender</label>
                    <select value={form.gender} onChange={e => update("gender", e.target.value)}>
                      <option value="">Select…</option>
                      <option>Men's</option><option>Women's</option><option>Unisex</option>
                    </select>
                  </div>
                </div>
                <div className="row2">
                  <div className="field">
                    <label>Brand</label>
                    <input type="text" placeholder="Levi's, Ralph Lauren, no brand…" value={form.brand} onChange={e => update("brand", e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Era / Age</label>
                    <select value={form.era} onChange={e => update("era", e.target.value)}>
                      <option value="">Select…</option>
                      <option>1950s or earlier</option><option>1960s</option>
                      <option>1970s</option><option>1980s</option><option>1990s</option>
                      <option>Y2K (2000–2005)</option><option>2005–2015</option>
                      <option>Recent (2015+)</option>
                    </select>
                  </div>
                </div>
                <div className="row3">
                  <div className="field">
                    <label>Size</label>
                    <input type="text" placeholder="XS / M / 32" value={form.size} onChange={e => update("size", e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Color</label>
                    <input type="text" placeholder="Navy blue" value={form.color} onChange={e => update("color", e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Material</label>
                    <input type="text" placeholder="100% cotton" value={form.material} onChange={e => update("material", e.target.value)} />
                  </div>
                </div>
                <div className="row2">
                  <div className="field">
                    <label>Condition *</label>
                    <select value={form.condition} onChange={e => update("condition", e.target.value)}>
                      <option value="">Select…</option>
                      <option>Excellent / Like new</option><option>Good — minor wear</option>
                      <option>Fair — visible wear</option><option>Distressed / As-is</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Asking price</label>
                    <input type="text" placeholder="$45 or leave blank" value={form.askingPrice} onChange={e => update("askingPrice", e.target.value)} />
                  </div>
                </div>
                <div className="field">
                  <label>What you paid for it (cost) — for profit tracking</label>
                  <input type="text" placeholder="$0 if it was free / your own closet" value={form.itemCost} onChange={e => update("itemCost", e.target.value)} />
                </div>
                <div className="field">
                  <label>Extra details</label>
                  <textarea placeholder="Original hardware, fading on shoulder, estate sale find…" value={form.description} onChange={e => update("description", e.target.value)} />
                </div>
              </div>

              <button className="analyze-btn" onClick={analyze} disabled={!form.itemName || !form.category || !form.condition || !form.sellerName || loading}>
                {loading ? "Analyzing…" : "Analyze & Generate Listing →"}
              </button>

              {loading && (
                <div className="loading-wrap">
                  <div className="loading-bar"><div className="loading-bar-fill" /></div>
                  <div className="loading-label">{LOAD_MSGS[loadMsg]}</div>
                </div>
              )}

              {result && !result.error && platform && (
                <div ref={resultRef} style={{ marginTop: "1.75rem" }}>
                  <div className="section-label">AI Recommendation</div>
                  <div className="result-card">
                    <div className="platform-badge" style={{ background: platform.color + "18", color: platform.color }}>
                      <div className="platform-icon" style={{ background: platform.color }}>{platform.icon}</div>
                      List on {platform.name} · {platform.fee} fee
                    </div>
                    <div className="seller-tag">
                      <span className="seller-dot">{initials(form.sellerName)}</span>
                      Listed by {form.sellerName}
                    </div>
                    {images.length > 0 && (
                      <div className="result-section" style={{ marginTop: 14 }}>
                        <div className="result-section-title">Photos ({images.length})</div>
                        <div className="result-images">{images.map((url, i) => <img key={i} src={url} alt={`Photo ${i+1}`} />)}</div>
                      </div>
                    )}
                    <hr className="divider" />
                    <div className="result-section">
                      <div className="result-section-title">Item details</div>
                      <div className="details-grid">
                        {[["Category","category"],["Brand","brand"],["Size","size"],["Color","color"],["Material","material"],["Condition","condition"],["Era","era"],["Gender","gender"]].map(([label, key]) => form[key] ? (
                          <div key={key} className="detail-chip">
                            <div className="detail-chip-label">{label}</div>
                            <input className="editable-field" style={{ padding: "4px 8px", fontSize: 12, marginTop: 2 }} value={form[key]} onChange={e => update(key, e.target.value)} />
                          </div>
                        ) : null)}
                      </div>
                    </div>
                    <hr className="divider" />
                    <div className="result-section">
                      <div className="result-section-title">Why this platform</div>
                      <div className="result-reasoning">{result.reasoning}</div>
                    </div>
                    {result.alternativePlatforms?.length > 0 && (
                      <div className="result-section">
                        <div className="result-section-title">Also consider</div>
                        <div className="alt-platforms">{result.alternativePlatforms.map(p => <span key={p} className="alt-pill">{p}</span>)}</div>
                      </div>
                    )}
                    <hr className="divider" />
                    <div className="result-section">
                      <div className="result-section-title">Suggested price</div>
                      <input className="editable-field" style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 600, padding: "8px 12px" }} value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                      <div className="price-note" style={{ marginTop: 6 }}>{result.pricingNote}</div>
                    </div>
                    <hr className="divider" />
                    <div className="result-section">
                      <div className="result-section-title">Listing title</div>
                      <input className="editable-field" style={{ fontWeight: 600 }} value={editTitle} onChange={e => setEditTitle(e.target.value)} maxLength={80} />
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{editTitle.length}/80 chars</div>
                    </div>
                    <div className="result-section">
                      <div className="result-section-title">Listing description</div>
                      <textarea className="editable-field editable-textarea" value={editDesc} onChange={e => setEditDesc(e.target.value)} />
                    </div>
                    <div className="result-section">
                      <div className="result-section-title">Hashtags</div>
                      <input className="editable-field" style={{ color: "var(--muted)", fontSize: 13 }} value={editHashtags} onChange={e => setEditHashtags(e.target.value)} />
                    </div>
                    <hr className="divider" />
                    <div className="btn-row">
                      {(result.platformKey === "etsy" || result.platformKey === "ebay") && (
                        <PostButton platformKey={result.platformKey} listing={buildPayload()} connectionStatus={connectionStatus} onPosted={handlePosted} />
                      )}
                      {result.platformKey !== "ebay" && connectionStatus.ebay && (
                        <PostButton platformKey="ebay" listing={buildPayload()} connectionStatus={connectionStatus} label="Post to eBay anyway →" onPosted={handlePosted} />
                      )}
                      {result.platformKey !== "ebay" && !connectionStatus.ebay && (
                        <button className="copy-btn" onClick={() => setTab("connect")} style={{ borderColor: ebayPlatform.color, color: ebayPlatform.color }}>
                          Connect eBay to auto-post →
                        </button>
                      )}
                      {result.platformKey !== "etsy" && result.platformKey !== "ebay" && (
                        <a className="go-btn" href={platform.url} target="_blank" rel="noopener noreferrer">Open {platform.name} →</a>
                      )}
                      <button className="copy-btn" onClick={copyListing}>Copy listing</button>
                      {copied && <span className="copied-msg">Copied!</span>}
                    </div>
                  </div>
                  <button className="copy-btn" style={{ width: "100%" }} onClick={reset}>+ List another item</button>
                </div>
              )}

              {result?.error && (
                <div className="result-card" style={{ marginTop: "1.5rem", color: "var(--red)" }}>{result.error}</div>
              )}
            </>
          )}

          {/* ── HISTORY ── */}
          {tab === "history" && (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                <button className={`filter-chip ${historySubTab === "items" ? "active" : ""}`} onClick={() => setHistorySubTab("items")}>All items</button>
                <button className={`filter-chip ${historySubTab === "analytics" ? "active" : ""}`} onClick={() => setHistorySubTab("analytics")}>Analytics</button>
              </div>

              {historySubTab === "analytics" ? (
                <AnalyticsDashboard history={history} />
              ) : (
                <>
                  <div className="search-row">
                    <input className="search-input" type="text" placeholder="Search items, sellers, brands…" value={search} onChange={e => setSearch(e.target.value)} />
                    {search && <button className="mini-btn" onClick={() => setSearch("")}>Clear</button>}
                    <button className="mini-btn" onClick={exportCSV} disabled={!history.length}>↓ CSV</button>
                  </div>

                  <div className="filter-row">
                    {[
                      { key: "all",       label: `All (${history.length})` },
                      { key: "available", label: `Available (${history.filter(i => (i.status||"available") === "available").length})` },
                      { key: "sold",      label: `Sold (${history.filter(i => (i.status||"available") === "sold").length})` },
                      { key: "cancelled", label: `Cancelled (${history.filter(i => i.status === "cancelled").length})` },
                    ].map(f => (
                      <button key={f.key} className={`filter-chip ${filter === f.key ? "active" : ""}`} onClick={() => setFilter(f.key)}>
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {historyLoading ? (
                    <div className="loading-wrap"><div className="loading-bar"><div className="loading-bar-fill" /></div><div className="loading-label">Loading…</div></div>
                  ) : filteredHistory.length === 0 ? (
                    <div className="empty"><div className="empty-icon">🧥</div><div className="empty-text">No items here yet.</div></div>
                  ) : (
                    <div className="history-grid">
                      {filteredHistory.map(item => {
                        const st = item.status || (item.sold ? "sold" : "available");
                        const sc = STATUS_CONFIG[st] || STATUS_CONFIG.available;
                        const p = item.platform_key ? PLATFORMS[item.platform_key] : null;
                        return (
                          <div
                            key={item.id}
                            className="history-card"
                            style={{ opacity: sc.opacity }}
                            onClick={() => {
                              setForm({ sellerName: item.seller_name||"", sellerId: item.seller_id||"", itemName: item.item_name||"", category: item.category||"", gender: item.gender||"", brand: item.brand||"", era: item.era||"", size: item.size||"", condition: item.condition||"", material: item.material||"", color: item.color||"", description: item.description||"", askingPrice: item.asking_price||"", itemCost: item.item_cost != null ? String(item.item_cost) : "" });
                              setImages(item.image_urls||[]);
                              setCurrentListingId(item.id);
                              const r = { platform: item.platform, platformKey: item.platform_key, reasoning: item.reasoning, alternativePlatforms: item.alternative_platforms, suggestedTitle: item.suggested_title, suggestedDescription: item.suggested_description, suggestedPrice: item.suggested_price, pricingNote: item.pricing_note, hashtags: item.hashtags };
                              setResult(r);
                              setEditTitle(item.suggested_title||""); setEditDesc(item.suggested_description||""); setEditPrice(item.suggested_price||""); setEditHashtags(item.hashtags||"");
                              setTab("list");
                            }}
                          >
                            <div className="history-card-inner">
                              {item.image_urls?.[0] ? <img className="history-img" src={item.image_urls[0]} alt={item.item_name} /> : <div className="history-img-placeholder">👕</div>}
                              <div className="history-body">
                                <div className="history-top">
                                  <div>
                                    <div className="history-name">{item.item_name}</div>
                                    <div className="history-seller"><span className="seller-dot" style={{ fontSize: 9 }}>{initials(item.seller_name)}</span> {item.seller_name}</div>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                    <span className="status-badge" style={{ background: sc.badgeBg, color: sc.badgeColor }}>{sc.label}</span>
                                    {p && st === "available" && <span className="platform-pill" style={{ background: p.color + "18", color: p.color }}>{p.name}</span>}
                                  </div>
                                </div>
                                <div className="history-meta">{[item.category, item.brand, item.era, item.suggested_price].filter(Boolean).join(" · ")}</div>
                                {item.ebay_url && (
                                  <a className="ebay-link" href={item.ebay_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>View on eBay ↗</a>
                                )}
                              </div>
                            </div>
                            <HistoryActions
                              item={item}
                              onStatusChange={handleStatusChange}
                              onDelete={handleDelete}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="footer">
          <div className="footer-brand">Closet Collective</div>
          <div className="footer-sub">Curated resale, together · closetcollective.club</div>
        </div>
      </div>

      {soldItem && (
        <SoldModal
          item={soldItem}
          members={members}
          onClose={() => setSoldItem(null)}
          onConfirm={confirmSale}
        />
      )}
    </>
  );
}
